import { ComplaintFlowProps, ComplaintFlowInputs } from 'features/complaint/views/flow/types'
import { useFormContext, useWatch } from 'react-hook-form'
import { Banner, Button, Checkbox, Form, Heading } from '@boltenergy-be/design-system'
import { Trans, useTranslation } from 'next-i18next/pages'
import React, { useState } from 'react'
import DataBlock from 'features/registration/steps/overview/data-block/DataBlock'
import styles from '../ComplaintFlow.module.scss'
import FilePreview from 'components/inputs/file-upload/FilePreview'
import { routes } from 'utils/routes'
import Link from 'components/link/Link'
import boltBackend from 'lib/bolt-backend'
import { CreateComplaintCaseRequestBody } from '@boltenergy-be/shared'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { useGetLanguage } from 'hooks/useGetLanguage'

const Overview = ({ setCurrentStep }: ComplaintFlowProps) => {
  // i18n
  const { t } = useTranslation('complaint', { keyPrefix: 'flow.steps.overview' })
  const { t: tX } = useTranslation(['common', 'validation'])
  const language = useGetLanguage()
  const uppercaseLanguage = useGetLanguage('uppercase')

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<'file' | 'submit' | 'catch' | null>(null)

  // React Hook Form
  const hookForm = useFormContext<ComplaintFlowInputs>()
  const { handleSubmit, register } = hookForm
  const contactInputs = useWatch({ control: hookForm.control, name: 'contact' })
  const complaintInputs = useWatch({ control: hookForm.control, name: 'complaint' })
  const acceptedAgreement = useWatch({ control: hookForm.control, name: 'privacyAgreement' })

  /**
   * Submit handler for the overview step of the complaint flow.
   * @param {Omit<ComplaintFlowInputs, "complaint">} inputs
   */
  const submitComplaint = async (inputs: ComplaintFlowInputs) => {
    const startTime = performance.now()
    setLoading(true)
    setError(null)

    try {
      const attachmentFiles = inputs.complaint.attachments?.length ? Array.from(inputs.complaint.attachments) : []
      const totalSize = attachmentFiles.reduce((total, file) => total + (file?.size || 0), 0)
      // Upload files to S3
      const s3Res = await Promise.all(
        attachmentFiles?.length ? attachmentFiles.map(async (file) => await boltBackend.uploadPresigned(file)) : []
      )

      if (s3Res.some(({ success, data }) => !success || !data)) {
        setError('file')
        trackEvent(
          { category: 'complaint', object: 'files', action: 'fail' },
          { amount_of_files: attachmentFiles.length, total_attachment_size_bytes: totalSize }
        )
        return
      }

      // Create complaint case
      const attachmentKeys = s3Res.map(({ data }) => data).filter((key): key is string => !!key)
      const body: CreateComplaintCaseRequestBody = {
        firstName: inputs.contact.firstName,
        lastName: inputs.contact.lastName,
        phone: inputs.contact.mobilePhone || undefined,
        email: inputs.contact.email,
        language: uppercaseLanguage,
        subject: inputs.complaint.subject,
        description: inputs.complaint.description,
        attachmentKeys
      }
      const { success, data } = await boltBackend.postComplaint(body)

      if (!success || !data?.caseId) {
        setError('submit')
        trackEvent(
          { category: 'complaint', object: 'submit', action: 'fail' },
          { amount_of_files: attachmentFiles.length, total_attachment_size_bytes: totalSize }
        )
        return
      }

      const totalDuration = performance.now() - startTime
      trackEvent(
        { category: 'complaint', object: 'submit', action: 'finish' },
        { duration_ms: totalDuration, amount_of_files: attachmentFiles.length, total_attachment_size_bytes: totalSize }
      )
      trackGTMEvent('complaint_submitted', { duration_ms: totalDuration })
      setCurrentStep('confirmation')
    } catch (e) {
      setError('catch')
      trackEvent({ category: 'complaint', action: 'fail' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <section>
      <header>
        <Heading as="h2">{t('title')}</Heading>
        <p>{t('description')}</p>
      </header>

      <Form onSubmit={handleSubmit(submitComplaint)}>
        <section className="grid-col-full flex-col gap-500">
          <header className="flex-row gap-400">
            <Heading as="h2" variant="h3" serif>
              {t('contact.title')}
            </Heading>

            <Button representation="link" onClick={() => setCurrentStep('contact')}>
              {tX('change')}
            </Button>
          </header>

          <DataBlock label={t('contact.personalData')}>
            <span>
              {contactInputs.firstName} {contactInputs.lastName}
            </span>
            <span>{contactInputs.email}</span>
            <span>{contactInputs.mobilePhone}</span>
          </DataBlock>
        </section>

        <section className="grid-col-full flex-col gap-500">
          <header className="flex-row gap-400">
            <Heading as="h2" variant="h3" serif>
              {t('complaint.title')}
            </Heading>

            <Button representation="link" onClick={() => setCurrentStep('complaint')}>
              {tX('change')}
            </Button>
          </header>

          <DataBlock label={t('complaint.subject')}>{complaintInputs.subject}</DataBlock>
          <DataBlock label={t('complaint.reason')}>{complaintInputs.description}</DataBlock>
          {!!complaintInputs.attachments?.length && (
            <DataBlock label={t('complaint.attachment', { count: complaintInputs.attachments.length })}>
              <dl className={styles.attachments}>
                {Array.from(complaintInputs.attachments).map((attachment) => (
                  <FilePreview key={attachment.name} file={attachment} />
                ))}
              </dl>
            </DataBlock>
          )}
        </section>

        <Checkbox
          {...register('privacyAgreement')}
          className="grid-col-full"
          id="acceptAgreement"
          label={
            <span>
              <Trans
                {...{ t }}
                i18nKey="privacyAgreement"
                components={[<Link key="0" variant="inline" target="_blank" href={routes(language).privacy} />]}
              />
            </span>
          }
        />

        {!!error && <Banner type="blocking" className="grid-col-full" title={tX('error')} />}

        <Form.Footer className="flex-row mt-400">
          <Button type="button" leadingIcon="arrowLeft" disabled={loading} variant="secondary" onClick={() => setCurrentStep('complaint')}>
            {tX('previous')}
          </Button>

          <Button trailingIcon="arrowRight" loading={loading} className="ml-auto" disabled={!acceptedAgreement}>
            {tX('send')}
          </Button>
        </Form.Footer>
      </Form>
    </section>
  )
}

export default Overview
